import { useState, useEffect, useCallback } from "react";
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import Container from "../Container";
import ProductCard from "../ProductCard";
import { Product } from "../../types";

interface Promotion {
  id: number;
  name: string;
  description?: string;
  banner_text?: string;
  image_url?: string;
  color: string;
  countdown_time_remaining?: {
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
  };
  time_remaining_to_end?: {
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
  };
}

interface PromotionsSectionProps {
  products: Product[];
  promotion?: Promotion;
  store_settings: any;
}

const PromotionsSection = ({ products, promotion, store_settings }: PromotionsSectionProps) => {
  const timerData = promotion?.countdown_time_remaining || promotion?.time_remaining_to_end;
  const [timeLeft, setTimeLeft] = useState(timerData || { days: 0, hours: 0, minutes: 0, seconds: 0 });

  const getColorClasses = (color: string) => {
    const colorMap = {
      red: {
        bg: 'bg-gradient-to-br from-red-50 via-pink-50 to-orange-50 dark:from-red-900/10 dark:via-pink-900/10 dark:to-orange-900/10',
        badge: 'from-red-500 to-orange-500',
        text: 'text-red-600 dark:text-red-400',
        timer: 'bg-red-600',
        separator: 'from-red-500 to-orange-500',
        overlay: 'bg-red-500',
        navHover: 'hover:bg-red-600',
        download: 'from-blue-500 to-purple-500'
      },
      green: {
        bg: 'bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 dark:from-green-900/10 dark:via-emerald-900/10 dark:to-teal-900/10',
        badge: 'from-green-500 to-emerald-500',
        text: 'text-green-600 dark:text-green-400',
        timer: 'bg-green-600',
        separator: 'from-green-500 to-emerald-500',
        overlay: 'bg-green-500',
        navHover: 'hover:bg-green-600',
        download: 'from-blue-500 to-purple-500'
      },
      purple: {
        bg: 'bg-gradient-to-br from-purple-50 via-violet-50 to-indigo-50 dark:from-purple-900/10 dark:via-violet-900/10 dark:to-indigo-900/10',
        badge: 'from-purple-500 to-violet-500',
        text: 'text-purple-600 dark:text-purple-400',
        timer: 'bg-purple-600',
        separator: 'from-purple-500 to-violet-500',
        overlay: 'bg-purple-500',
        navHover: 'hover:bg-purple-600',
        download: 'from-blue-500 to-purple-500'
      }
    };
    return colorMap[color as keyof typeof colorMap] || colorMap.red;
  };

  const colorClasses = getColorClasses(promotion?.color || 'red');

  useEffect(() => {
    if (!timerData) return;

    const timer = setInterval(() => {
      setTimeLeft(prev => {
        let { days, hours, minutes, seconds } = prev;
        if (seconds > 0) {
          seconds--;
        } else if (minutes > 0) {
          minutes--;
          seconds = 59;
        } else if (hours > 0) {
          hours--;
          minutes = 59;
          seconds = 59;
        } else if (days > 0) {
          days--;
          hours = 23;
          minutes = 59;
          seconds = 59;
        } else {
          clearInterval(timer);
          return { days: 0, hours: 0, minutes: 0, seconds: 0 };
        }
        return { days, hours, minutes, seconds };
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timerData]);

  if (!products || products.length === 0) {
    return null;
  }

  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: true, align: 'start', slidesToScroll: 1 },
    [Autoplay({ delay: 3500, stopOnInteraction: false })]
  );

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  return (
    <section className={`py-20 ${colorClasses.bg} relative overflow-hidden`}>
      <style>{`
        .embla {
          overflow: hidden;
        }
        .embla__container {
          display: flex;
        }
        .embla__slide {
          flex: 0 0 25% !important;
          min-width: 0;
          padding-left: 10px;
          padding-right: 10px;
        }
        .embla__prev, .embla__next {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          background: transparent;
          border: none;
          border-radius: 50%;
          width: 50px;
          height: 50px;
          font-size: 24px;
          color: #6b7280;
          cursor: pointer;
          transition: all 0.3s ease;
          z-index: 10;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
        }
        .embla__prev:hover, .embla__next:hover {
          background: rgba(255, 255, 255, 1);
          transform: translateY(-50%) scale(1.1);
        }
        .embla__prev {
          left: -25px;
        }
        .embla__next {
          right: -25px;
        }
        @media (max-width: 1279px) {
          .embla__slide {
            flex: 0 0 33.333% !important;
          }
        }
        @media (max-width: 768px) {
          .embla__slide {
            flex: 0 0 50% !important;
          }
        }
        @media (max-width: 480px) {
          .embla__slide {
            flex: 0 0 50% !important;
          }
        }
        @media (max-width: 768px) {
          .embla__slide {
            flex: 0 0 50%;
          }
          .embla__prev, .embla__next {
            top: 50%;
            bottom: auto;
            transform: translateY(-50%);
            background-color: rgba(255, 255, 255, 0.8);
            box-shadow: 0 4px 12px rgba(0,0,0,0.15);
            width: 50px;
            height: 50px;
            border-radius: 50%;
            color: #333;
            font-size: 28px;
          }
          .embla__prev {
            left: 10px;
          }
          .embla__next {
            right: 10px;
          }
          .embla__prev:hover, .embla__next:hover {
            background-color: rgba(255, 255, 255, 0.9);
            transform: translateY(-50%) scale(1.05);
          }
          .embla__buttons {
            display: none;
          }
        }
        @media (max-width: 480px) {
          .embla__slide {
            flex: 0 0 80%;
          }
        }
      `}</style>
      {/* Animated background elements */}
      <div className="absolute top-0 left-0 w-full h-full">
        <div className={`absolute top-20 left-20 w-24 h-24 ${promotion?.color === 'red' ? 'bg-red-400/20' : promotion?.color === 'green' ? 'bg-green-400/20' : 'bg-purple-400/20'} rounded-full animate-bounce`} />
        <div className={`absolute bottom-20 right-20 w-32 h-32 ${promotion?.color === 'red' ? 'bg-orange-400/20' : promotion?.color === 'green' ? 'bg-teal-400/20' : 'bg-indigo-400/20'} rounded-full animate-pulse`} />
        <div className={`absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 ${promotion?.color === 'red' ? 'bg-pink-400/10' : promotion?.color === 'green' ? 'bg-emerald-400/10' : 'bg-violet-400/10'} rounded-full blur-3xl`} />
      </div>

      <Container className="relative">
        {/* Promotion Image Decoration */}
        {promotion?.image_url && (
          <div className="absolute top-10 right-10 z-20 hidden md:block">
            <img
              src={promotion.image_url}
              alt={promotion.name}
              className="w-48 h-48 object-contain rounded-lg shadow-lg"
            />
          </div>
        )}

        <div className="text-center mb-16">
          {/* Flash Sale Badge */}
          <div className={`inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r ${colorClasses.badge} rounded-full text-white font-bold mb-6 animate-pulse`}>
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M12.395 2.553a1 1 0 00-1.45-.385c-.345.23-.614.558-.822.88-.214.33-.403.713-.57 1.116-.334.804-.614 1.768-.84 2.734a31.365 31.365 0 00-.613 3.58 2.64 2.64 0 01-.945-1.067c-.328-.68-.398-1.534-.398-2.654A1 1 0 005.05 6.05 6.981 6.981 0 003 11a7 7 0 1011.95-4.95c-.592-.591-.98-.985-1.348-1.467-.363-.476-.724-1.063-1.207-2.03zM12.12 15.12A3 3 0 017 13s.879.5 2.5.5c0-1 .5-4 1.25-4.5.5 1 .786 1.293 1.371 1.879A2.99 2.99 0 0113 13a2.99 2.99 0 01-.879 2.121z"
                clipRule="evenodd"
              />
            </svg>
            🔥 OFERTAS ESPECIALES 🔥
          </div>

          <h3 className="text-2xl md:text-3xl font-bold mb-6">
            <span className={colorClasses.text}>{promotion?.name || "¡Descuentos"}</span>{" "}
            {promotion?.name ? "" : "Increíbles"}
          </h3>

          <p className="text-lg text-muted-foreground max-w-3xl mx-auto leading-relaxed mb-4">
            {promotion?.description || "Aprovecha nuestros descuentos por tiempo limitado. ¡Los mejores precios del año te están esperando!"}
          </p>

          {promotion?.banner_text && (
            <p className={`text-xl font-semibold ${colorClasses.text} max-w-3xl mx-auto leading-relaxed mb-8`}>
              {promotion.banner_text}
            </p>
          )}

          {/* Countdown Timer */}
          {timerData && (
            <div className="flex justify-center items-center gap-3 mb-8">
              <div className="text-center">
                <div className={`${colorClasses.timer} text-white rounded-xl px-4 py-3 font-bold text-xl shadow-lg`}>
                  {timeLeft.days.toString().padStart(2, '0')}
                </div>
                <div className="text-sm text-muted-foreground mt-2 font-medium">DÍAS</div>
              </div>
              <div className={`${colorClasses.text} text-3xl font-.bold mx-1`}>:</div>
              <div className="text-center">
                <div className={`${colorClasses.timer} text-white rounded-xl px-4 py-3 font-bold text-xl shadow-lg`}>
                  {timeLeft.hours.toString().padStart(2, '0')}
                </div>
                <div className="text-sm text-muted-foreground mt-2 font-medium">HORAS</div>
              </div>
              <div className={`${colorClasses.text} text-3xl font-bold mx-1`}>:</div>
              <div className="text-center">
                <div className={`${colorClasses.timer} text-white rounded-xl px-4 py-3 font-bold text-xl shadow-lg`}>
                  {timeLeft.minutes.toString().padStart(2, '0')}
                </div>
                <div className="text-sm text-muted-foreground mt-2 font-medium">MIN</div>
              </div>
              <div className={`${colorClasses.text} text-3xl font-bold mx-1`}>:</div>
              <div className="text-center">
                <div className={`${colorClasses.timer} text-white rounded-xl px-4 py-3 font-bold text-xl shadow-lg`}>
                  {timeLeft.seconds.toString().padStart(2, '0')}
                </div>
                <div className="text-sm text-muted-foreground mt-2 font-medium">SEG</div>
              </div>
            </div>
          )}

          <div className={`w-24 h-1 bg-gradient-to-r ${colorClasses.separator} rounded-full mx-auto`} />
        </div>
      </Container>

      {products.length >= 4 ? (
        <div className="relative max-w-[1240px] mx-auto w-full mt-10 mb-24">
          <div className="embla w-full" ref={emblaRef}>
            <div className="embla__container">
              {products.map((product) => (
                <div key={product.id} className="embla__slide">
                  <div className="px-2">
                  <div className="h-full relative">
                    <ProductCard product={product} className="h-full" store_settings={store_settings} />
                    <div className={`absolute -top-2 -right-2 z-30 ${colorClasses.overlay} text-white text-xs font-bold px-2 py-1 rounded-full animate-bounce`}>

                    </div>
                  </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="embla__buttons hidden md:block">
            <button
              className="embla__prev"
              onClick={scrollPrev}
            >
              ‹
            </button>
            <button
              className="embla__next"
              onClick={scrollNext}
            >
              ›
            </button>
          </div>
          <div className="md:hidden mt-8 flex items-center justify-center gap-4">
            <button
              className="embla__prev"
              onClick={scrollPrev}
            >
              ‹
            </button>
            <button
              className="embla__next"
              onClick={scrollNext}
            >
              ›
            </button>
          </div>
        </div>
      ) : (
        <Container>
          <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {products.map((product, index) => (
              <div
                key={product.id}
                className="relative"
                style={{
                  animationDelay: `${index * 100}ms`,
                  animation: "fadeInUp 0.6s ease-out both",
                }}
              >
                  <div className={`absolute -top-2 -right-2 z-30 ${colorClasses.overlay} text-white text-xs font-bold px-2 py-1 rounded-full animate-bounce`}>
                    ¡OFERTA!
                  </div>
                <ProductCard product={product} className="h-full" store_settings={store_settings} />
              </div>
            ))}
          </div>
        </Container>
        )}
    </section>
  );
};

export default PromotionsSection;